// src/components/messaging/MessageBubble.tsx
import React from 'react';
import { Message } from '../../types/message';

interface MessageBubbleProps {
  message: Message;
  isOwnMessage: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isOwnMessage }) => {
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Enhanced status icon with better visual indicators
  const getStatusIcon = (status: Message['status']) => {
    if (status.read) {
      // Double checkmark (read) - two checkmarks side by side
      return (
        <span className="inline-flex items-center -space-x-1">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </span>
      );
    }
    if (status.delivered) {
      // Single checkmark (delivered) - light blue
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      );
    }
    // Clock icon (sent/pending) - gray
    return (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
    );
  };

  const getStatusColor = (status: Message['status']) => {
    if (status.read) return 'text-white/80'; // White for read
    if (status.delivered) return 'text-white/70'; // Light white for delivered
    return 'text-white/50'; // Gray for sent/pending
  };

  const getStatusTooltip = (status: Message['status']) => {
    if (status.read && status.readAt) {
      const readDate = new Date(status.readAt);
      return `Read at ${readDate.toLocaleTimeString()}`;
    }
    if (status.read) return 'Read';
    if (status.delivered) return 'Delivered';
    return 'Sent';
  };

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-xs lg:max-w-md px-4 py-2 shadow-sm ${
        isOwnMessage 
          ? 'bg-steel text-white border-2 border-coffee rounded-2xl rounded-tr-none' 
          : 'bg-cream text-coffee border-2 border-coffee rounded-2xl rounded-tl-none'
      }`}>
        {/* Message Content */}
        <div className="text-sm">
          {message.content}
        </div>
        
        {/* Message Footer */}
        <div className={`flex items-center justify-between mt-1 text-xs ${
          isOwnMessage ? 'text-white/80' : 'text-coffee/60'
        }`}>
          <span>{formatTime(message.timestamp)}</span>
          {isOwnMessage && (
            <span 
              className={`ml-2 flex items-center ${getStatusColor(message.status)}`}
              title={getStatusTooltip(message.status)}
            >
              {getStatusIcon(message.status)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;